import { apiEvents } from "@/api/event.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { candidateActionsKeys } from "@/queries/recruit/use-candidate-actions.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEventAttendeeResponseStatuses,
  ApiEventsBusyFilter,
  ApiEventFilter,
  ApiEventSavePayload,
  ApiEventTypes,
} from "@/types/event.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const eventsKeys = createQueryKeys("event", {
  busy: (filter: ApiEventsBusyFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const batches = [];
      for (let i = 0; i < filter.employee_ids.length; i += 50) {
        batches.push(
          apiEvents.busy({ ...filter, employee_ids: filter.employee_ids.slice(i, i + 50) }, signal)
        );
      }
      const results = await Promise.all(batches);
      return results.flatMap((result) => {
        if (result.error || !result.data) throw new ApiErrorException(result.error);
        return result.data;
      });
    },
  }),
  filter: (filter: ApiEventFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiEvents.filter(filter, signal);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useEvents = (filter: ApiEventFilter, enabled: boolean = true) => {
  return useQuery({
    ...eventsKeys.filter(filter),
    enabled: enabled,
  });
};

export const useEventsSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: ApiEventSavePayload }) => {
      const { data, error } = id
        ? await apiEvents.update(id, payload)
        : await apiEvents.create(payload);

      if (error || !data) throw new ApiErrorException(error);

      return data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: eventsKeys.busy._def });
      void queryClient.invalidateQueries({ queryKey: eventsKeys.filter._def });
      if (variables.payload.type === ApiEventTypes.interview) {
        void queryClient.invalidateQueries({ queryKey: candidateActionsKeys.list._def }); // TODO how to invalidate only candidate's actions?
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEventsResponse = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      response,
    }: {
      id: number;
      response: ApiEventAttendeeResponseStatuses;
    }) => {
      const { data, error } = await apiEvents.updateResponse(id, response);

      if (error || !data) throw new ApiErrorException(error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.filter._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEventsDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (eventId: number) => {
      const { success, error } = await apiEvents.delete(eventId);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.filter._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeesBusy = (filter: ApiEventsBusyFilter, enabled = true) =>
  useQuery({
    ...eventsKeys.busy(filter),
    enabled: enabled && filter.employee_ids.length > 0,
    staleTime: 30_000,
  });
